import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  Scale,
  ChevronDown,
  Info,
  AlertTriangle,
  BarChart3,
  Users,
} from "lucide-react";

// Helper functions defined ONCE at the top level
const checkResponse = (value) => {
  const normalized = String(value || "")
    .toLowerCase()
    .trim();
  if (normalized.startsWith("y")) return "yes";
  if (normalized.startsWith("n") && !normalized.includes("n/a")) return "no";
  return "na";
};

const isInTherapy = (value) => {
  const n = Number(value);
  return n === 3 || n === 4;
};

const getBaseName = (fullName) => {
  if (!fullName) return null;
  return fullName.replace(/\s*\([^)]*\)$/, "").trim();
};

const RCProbationTab = ({ organizationId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRiskFactor, setSelectedRiskFactor] = useState("trauma");
  const [isRiskFactorDropdownOpen, setIsRiskFactorDropdownOpen] =
    useState(false);
  const [selectedMetric, setSelectedMetric] = useState("recidivism");
  const [isMetricDropdownOpen, setIsMetricDropdownOpen] = useState(false);

  const [probationData, setProbationData] = useState({
    probationOverview: [],
    probationTimeEffects: {
      earlyRecovery: { probation: {}, noProbation: {} },
      midRecovery: { probation: {}, noProbation: {} },
      establishedRecovery: { probation: {}, noProbation: {} },
      longTermRecovery: { probation: {}, noProbation: {} },
    },
    recidivismAnalysis: {
      probationRecidivism: {},
      noProbationRecidivism: {},
    },
    allRiskFactorInteractions: {},
    domainImpacts: [],
    probationOfficerAnalysis: {},
    violationPatterns: {},
    complianceMetrics: {},
  });

  const riskFactorOptions = [
    {
      id: "trauma",
      name: "Childhood Trauma",
      withLabel: "Probation/Parole + Trauma",
      withoutLabel: "Probation/Parole + No Trauma",
    },
    {
      id: "homeless",
      name: "Homeless ≥1 Year",
      withLabel: "Probation/Parole + Homeless",
      withoutLabel: "Probation/Parole + No Homeless",
    },
    {
      id: "prison",
      name: "Prison History",
      withLabel: "Probation/Parole + Prison",
      withoutLabel: "Probation/Parole + No Prison",
    },
    {
      id: "diagnosis",
      name: "Co-occurring Diagnosis",
      withLabel: "Probation/Parole + Co-occurring",
      withoutLabel: "Probation/Parole + No Co-occurring",
    },
    {
      id: "mat",
      name: "Medication-Assisted Treatment",
      withLabel: "Probation/Parole + MAT",
      withoutLabel: "Probation/Parole + No MAT",
    },
    {
      id: "therapy",
      name: "In Therapy",
      withLabel: "Probation/Parole + Therapy",
      withoutLabel: "Probation/Parole + No Therapy",
    },
  ];

  const metricOptions = [
    {
      id: "relapse",
      name: "Relapse %",
      yAxisLabel: "Relapse Percentage",
      color: "#3B82F6",
    },
    {
      id: "rcScore",
      name: "RC Score",
      yAxisLabel: "Recovery Capital Score",
      color: "#10B981",
    },
    {
      id: "recidivism",
      name: "Recidivism %",
      yAxisLabel: "Recidivism Percentage",
      color: "#EF4444",
    },
  ];

  const calculateProbationAnalysis = async (organizationId) => {
    try {
      const { collection, getDocs } = await import("firebase/firestore");
      const { db } = await import("../../utils/firebase");

      const [alumniSnapshot, residentSnapshot, rcSnapshot] = await Promise.all([
        getDocs(collection(db, `organizations/${organizationId}/alumni`)),
        getDocs(collection(db, `organizations/${organizationId}/residents`)),
        getDocs(
          collection(db, `organizations/${organizationId}/recoveryCapital`)
        ),
      ]);

      // --- PIPELINE 1: SIMPLE ALUMNI PROCESSING FOR RECIDIVISM ---
      const allAlumni = [];

      alumniSnapshot.docs.forEach((doc) => {
        const data = doc.data();

        // ONLY filter out duplicates (names with parentheses)
        if (data.fullName && data.fullName.match(/\s*\([^)]*\)$/)) {
          return; // Skip this one - it's a duplicate
        }

        // Add to alumni list with probation status
        allAlumni.push({
          ...data,
          id: doc.id,
          currentResident: false,
          isOnProbationParole: !!(data.poFirstName || data.poLastName),
          hasRecidivism: !!data.rearrestDate,
        });
      });

      // Calculate recidivism for ALL alumni
      const calculateRecidivismForAllAlumni = () => {
        const probationAlumni = allAlumni.filter((a) => a.isOnProbationParole);
        const nonProbationAlumni = allAlumni.filter(
          (a) => !a.isOnProbationParole
        );

        const probationRecidivism = {
          total: probationAlumni.length,
          recidivism: probationAlumni.filter((a) => a.hasRecidivism).length,
          rate:
            probationAlumni.length > 0
              ? (probationAlumni.filter((a) => a.hasRecidivism).length /
                  probationAlumni.length) *
                100
              : 0,
        };

        const noProbationRecidivism = {
          total: nonProbationAlumni.length,
          recidivism: nonProbationAlumni.filter((a) => a.hasRecidivism).length,
          rate:
            nonProbationAlumni.length > 0
              ? (nonProbationAlumni.filter((a) => a.hasRecidivism).length /
                  nonProbationAlumni.length) *
                100
              : 0,
        };

        return { probationRecidivism, noProbationRecidivism };
      };

      const recidivismAnalysis = calculateRecidivismForAllAlumni();

      // Add this after your recidivism analysis calculation
      const calculateTransitionAnalysis = () => {
        // Get all people who were ever on probation/parole
        const everOnProbation = [];
        let currentlySupervised = 0; // Counter for current residents only

        // Check residents
        residentSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          const isCurrentlySupervised = !!(
            data.poFirstName?.trim() || data.poLastName?.trim()
          );

          if (
            data.poFirstName ||
            data.poLastName ||
            (data.probationHistory && data.probationHistory.length > 0)
          ) {
            const baseName = getBaseName(data.fullName) || doc.id;
            if (!everOnProbation.find((p) => p.baseName === baseName)) {
              everOnProbation.push({
                baseName,
                ...data,
                currentResident: true,
                hasTransitionedOff: !!(
                  data.probationHistory &&
                  data.probationHistory.length > 0 &&
                  !data.poFirstName?.trim() &&
                  !data.poLastName?.trim()
                ),
                isCurrentlySupervised,
              });

              if (isCurrentlySupervised) {
                currentlySupervised++; // Count only current residents who are supervised
              }
            }
          }
        });

        // Check alumni
        alumniSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          if (
            data.poFirstName ||
            data.poLastName ||
            (data.probationHistory && data.probationHistory.length > 0)
          ) {
            const baseName = getBaseName(data.fullName) || doc.id;
            const existingIndex = everOnProbation.findIndex(
              (p) => p.baseName === baseName
            );

            const person = {
              baseName,
              ...data,
              currentResident: false,
              hasTransitionedOff: !!(
                data.probationHistory &&
                data.probationHistory.length > 0 &&
                !data.poFirstName?.trim() &&
                !data.poLastName?.trim()
              ),
              isCurrentlySupervised: false, // Alumni are not currently supervised
            };

            if (existingIndex >= 0) {
              // Don't overwrite resident data - keep residents as priority
              if (!everOnProbation[existingIndex].currentResident) {
                everOnProbation[existingIndex] = person; // Only overwrite if existing is also alumni
              }
            } else {
              everOnProbation.push(person);
            }
          }
        });

        const successfulTransitions = everOnProbation.filter(
          (p) => p.hasTransitionedOff
        ).length;
        const totalEverOnProbation = everOnProbation.length;
        const transitionRate =
          totalEverOnProbation > 0
            ? (successfulTransitions / totalEverOnProbation) * 100
            : 0;

        // Calculate overall probation percentage (all people, deduplicated)
        const allPeople = new Map();

        // Add all residents first
        residentSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          if (data.fullName && data.fullName.match(/\s*\([^)]*\)$/)) return; // Skip duplicates

          const baseName = getBaseName(data.fullName) || doc.id;
          allPeople.set(baseName, {
            ...data,
            currentResident: true,
            isOnProbationParole: !!(
              data.poFirstName?.trim() || data.poLastName?.trim()
            ),
          });
        });

        // Add alumni (overwrite if exists)
        alumniSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          if (data.fullName && data.fullName.match(/\s*\([^)]*\)$/)) return; // Skip duplicates

          const baseName = getBaseName(data.fullName) || doc.id;
          allPeople.set(baseName, {
            ...data,
            currentResident: false,
            isOnProbationParole: !!(
              data.poFirstName?.trim() || data.poLastName?.trim()
            ),
          });
        });

        const allPeopleArray = Array.from(allPeople.values());
        const onProbationCount = allPeopleArray.filter(
          (p) => p.isOnProbationParole
        ).length;
        const notOnProbationCount = allPeopleArray.length - onProbationCount;
        const probationPercentage =
          allPeopleArray.length > 0
            ? (onProbationCount / allPeopleArray.length) * 100
            : 0;

        return {
          successfulTransitions,
          totalEverOnProbation,
          transitionRate,
          currentlySupervised, // Add this for current residents only
          // Add these new values
          overallProbationStats: {
            onProbationCount,
            notOnProbationCount,
            totalPeople: allPeopleArray.length,
            probationPercentage,
          },
        };
      };

      const transitionAnalysis = calculateTransitionAnalysis();

      // --- PIPELINE 2: COMPLEX PROCESSING FOR RECOVERY CAPITAL ANALYSIS ---
      const residentProfiles = new Map();

      // Process residents
      residentSnapshot.docs.forEach((d) => {
        const data = d.data();
        // Use document ID as a fallback key if fullName is missing
        const key = getBaseName(data.fullName) || d.id;

        const existing = residentProfiles.get(key);
        residentProfiles.set(key, {
          ...data,
          currentResident: true,
          isOnProbationParole: !!(data.poFirstName || data.poLastName),
          probationOfficer:
            data.poFirstName && data.poLastName
              ? `${data.poFirstName} ${data.poLastName}`
              : null,
          occurrences: (existing?.occurrences || 0) + 1,
        });
      });

      // Process alumni, overwriting resident data if a person is now an alumnus
      alumniSnapshot.docs.forEach((d) => {
        const data = d.data();
        // Use document ID as a fallback key if fullName is missing
        const key = getBaseName(data.fullName) || d.id;

        const existing = residentProfiles.get(key);
        residentProfiles.set(key, {
          ...existing,
          ...data,
          currentResident: false, // Alumni status takes precedence
          isOnProbationParole: !!(data.poFirstName || data.poLastName),
          probationOfficer:
            data.poFirstName && data.poLastName
              ? `${data.poFirstName} ${data.poLastName}`
              : null,
          hasRecidivism: !!data.rearrestDate,
          recidivismDate: data.rearrestDate,
          occurrences: (existing?.occurrences || 0) + 1,
        });
      });

      // --- RECOVERY CAPITAL ANALYSIS (FOR ALL OTHER CHARTS) ---
      if (rcSnapshot.empty) {
        return {
          probationOverview: [],
          probationTimeEffects: {
            earlyRecovery: {},
            midRecovery: {},
            establishedRecovery: {},
            longTermRecovery: {},
          },
          recidivismAnalysis,
          allRiskFactorInteractions: {},
          domainImpacts: [],
          probationOfficerAnalysis: {},
          complianceMetrics: {},
        };
      }

      const residentNameMap = new Map();
      residentProfiles.forEach((profile, baseName) => {
        if (profile.fullName) {
          residentNameMap.set(profile.fullName.toLowerCase().trim(), baseName);
          residentNameMap.set(baseName.toLowerCase().trim(), baseName);
        }
      });

      const rawData = rcSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      const enhancedData = rawData
        .map((assessment) => {
          const residentName = assessment.residentName || assessment.fullName;
          let baseName = residentName
            ? residentNameMap.get(residentName.toLowerCase().trim()) ||
              getBaseName(residentName)
            : null;
          const profile = baseName ? residentProfiles.get(baseName) : null;
          if (!profile) return null;
          return { ...assessment, baseName, ...profile };
        })
        .filter(Boolean);

      const cleanedData = enhancedData.filter((d) => {
        if (!d.sobrietyDate) return false;
        try {
          const assessmentDate = d.timestamp?.toDate
            ? d.timestamp.toDate()
            : new Date(d.timestamp);
          const sobrietyDate = d.sobrietyDate?.toDate
            ? d.sobrietyDate.toDate()
            : new Date(d.sobrietyDate?.seconds * 1000 || d.sobrietyDate);
          return (
            !isNaN(assessmentDate.getTime()) &&
            !isNaN(sobrietyDate.getTime()) &&
            sobrietyDate <= assessmentDate
          );
        } catch {
          return false;
        }
      });

      const calculateGroupMetrics = (group) => {
        const uniqueResidents = new Map();
        group.forEach((assessment) => {
          const baseName = assessment.baseName;
          if (
            !uniqueResidents.has(baseName) ||
            new Date(assessment.timestamp) >
              new Date(uniqueResidents.get(baseName).timestamp)
          ) {
            uniqueResidents.set(baseName, assessment);
          }
        });

        const uniqueResidentsArray = Array.from(uniqueResidents.values());
        const alumni = uniqueResidentsArray.filter((d) => !d.currentResident);
        const soberCount = alumni.filter(
          (d) => d.sobrietyStatus === "Sober"
        ).length;
        const recidivismCount = alumni.filter((d) => d.hasRecidivism).length;
        const avgRC =
          group.length > 0
            ? group.reduce(
                (sum, d) => sum + (Number(d.recoveryCapitalTotal) || 0),
                0
              ) / group.length
            : 0;
        const sobrietyRate =
          alumni.length > 0 ? (soberCount / alumni.length) * 100 : 0;
        const relapseRate = alumni.length > 0 ? 100 - sobrietyRate : 0;
        const recidivismRate =
          alumni.length > 0 ? (recidivismCount / alumni.length) * 100 : 0;

        return {
          sobrietyRate,
          relapseRate,
          recidivismRate,
          avgRC,
          totalAlumni: alumni.length,
          totalAssessments: group.length,
          uniqueResidents: uniqueResidentsArray.length,
          soberCount,
          recidivismCount,
        };
      };

      const probationUsers = cleanedData.filter((d) => d.isOnProbationParole);
      const nonProbationUsers = cleanedData.filter(
        (d) => !d.isOnProbationParole
      );
      const probationMetrics = calculateGroupMetrics(probationUsers);
      const nonProbationMetrics = calculateGroupMetrics(nonProbationUsers);
      const probationOverview = [
        { name: "On Probation/Parole", ...probationMetrics },
        { name: "Not on Probation/Parole", ...nonProbationMetrics },
      ];

      const timePointBoundaries = {
        "0-90 days": { start: 0, end: 90 },
        "91-180 days": { start: 91, end: 180 },
        "181-270 days": { start: 181, end: 270 },
        "271-365 days": { start: 271, end: 365 },
      };
      const probationTimeEffects = {};
      Object.entries(timePointBoundaries).forEach(
        ([label, { start, end }], index) => {
          const periodNames = [
            "earlyRecovery",
            "midRecovery",
            "establishedRecovery",
            "longTermRecovery",
          ];
          const filterByTime = (d) => {
            if (!d.sobrietyDate) return false;
            const daysSober =
              (new Date(
                d.timestamp?.toDate ? d.timestamp.toDate() : d.timestamp
              ) -
                new Date(
                  d.sobrietyDate?.toDate
                    ? d.sobrietyDate.toDate()
                    : d.sobrietyDate
                )) /
              (1000 * 60 * 60 * 24);
            return daysSober >= start && daysSober <= end;
          };
          probationTimeEffects[periodNames[index]] = {
            probation: calculateGroupMetrics(
              cleanedData.filter(
                (d) => d.isOnProbationParole && filterByTime(d)
              )
            ),
            noProbation: calculateGroupMetrics(
              cleanedData.filter(
                (d) => !d.isOnProbationParole && filterByTime(d)
              )
            ),
          };
        }
      );

      const calculateRiskFactorInteractions = () => {
        const interactions = {};
        const probationAlumniWithAssessments = cleanedData.filter(
          (d) => d.isOnProbationParole && !d.currentResident
        );

        riskFactorOptions.forEach((factor) => {
          let withFactor, withoutFactor;
          switch (factor.id) {
            case "trauma":
              withFactor = probationAlumniWithAssessments.filter(
                (d) => checkResponse(d.childhoodTrauma) === "yes"
              );
              withoutFactor = probationAlumniWithAssessments.filter(
                (d) => checkResponse(d.childhoodTrauma) === "no"
              );
              break;
            case "homeless":
              withFactor = probationAlumniWithAssessments.filter(
                (d) => checkResponse(d.homelessOneYear) === "yes"
              );
              withoutFactor = probationAlumniWithAssessments.filter(
                (d) => checkResponse(d.homelessOneYear) === "no"
              );
              break;
            case "prison":
              withFactor = probationAlumniWithAssessments.filter(
                (d) => checkResponse(d.overOneYearInPrison) === "yes"
              );
              withoutFactor = probationAlumniWithAssessments.filter(
                (d) => checkResponse(d.overOneYearInPrison) === "no"
              );
              break;
            case "diagnosis":
              withFactor = probationAlumniWithAssessments.filter(
                (d) => checkResponse(d.coOccurringDiagnosis) === "yes"
              );
              withoutFactor = probationAlumniWithAssessments.filter(
                (d) => checkResponse(d.coOccurringDiagnosis) === "no"
              );
              break;
            case "mat":
              withFactor = probationAlumniWithAssessments.filter(
                (d) => d.isMAT === true
              );
              withoutFactor = probationAlumniWithAssessments.filter(
                (d) => d.isMAT === false
              );
              break;
            case "therapy":
              withFactor = probationAlumniWithAssessments.filter((d) =>
                isInTherapy(d.therapy)
              );
              withoutFactor = probationAlumniWithAssessments.filter(
                (d) => !isInTherapy(d.therapy)
              );
              break;
            default:
              withFactor = [];
              withoutFactor = [];
              break;
          }
          interactions[factor.id] = {
            name: factor.name,
            probationWith: calculateGroupMetrics(withFactor),
            probationWithout: calculateGroupMetrics(withoutFactor),
          };
        });
        return interactions;
      };

      // 4. Domain Impact Analysis
      const calculateDomainImpacts = () => {
        const probationUsers = cleanedData.filter((d) => d.isOnProbationParole);
        const nonProbationUsers = cleanedData.filter(
          (d) => !d.isOnProbationParole
        );

        const domains = [
          { name: "Social Capital", key: "socialCapitalScore" },
          { name: "Physical Capital", key: "physicalCapitalScore" },
          { name: "Individual Capital", key: "individualCapitalScore" },
          { name: "General Capital", key: "generalCapitalScore" },
          { name: "Spiritual Capital", key: "spiritualCapitalScore" },
        ];

        return domains.map((domain) => {
          const probationAvg =
            probationUsers.length > 0
              ? probationUsers.reduce(
                  (sum, d) => sum + (Number(d[domain.key]) || 0),
                  0
                ) / probationUsers.length
              : 0;

          const nonProbationAvg =
            nonProbationUsers.length > 0
              ? nonProbationUsers.reduce(
                  (sum, d) => sum + (Number(d[domain.key]) || 0),
                  0
                ) / nonProbationUsers.length
              : 0;

          const percentDiff =
            nonProbationAvg > 0
              ? ((probationAvg - nonProbationAvg) / nonProbationAvg) * 100
              : 0;

          return {
            domain: domain.name,
            probationAvg: parseFloat(probationAvg.toFixed(1)),
            nonProbationAvg: parseFloat(nonProbationAvg.toFixed(1)),
            percentDiff: parseFloat(percentDiff.toFixed(1)),
          };
        });
      };

      // 5. Probation Officer Analysis
      const calculateProbationOfficerAnalysis = () => {
        const officerGroups = {};

        cleanedData
          .filter((d) => d.isOnProbationParole && d.probationOfficer)
          .forEach((d) => {
            if (!officerGroups[d.probationOfficer]) {
              officerGroups[d.probationOfficer] = [];
            }
            officerGroups[d.probationOfficer].push(d);
          });

        return Object.entries(officerGroups)
          .map(([officer, residents]) => ({
            officer,
            ...calculateGroupMetrics(residents),
          }))
          .sort((a, b) => b.totalAssessments - a.totalAssessments);
      };

      return {
        probationOverview,
        probationTimeEffects,
        allRiskFactorInteractions: calculateRiskFactorInteractions(),
        domainImpacts: calculateDomainImpacts(),
        probationOfficerAnalysis: calculateProbationOfficerAnalysis(),
        recidivismAnalysis: recidivismAnalysis,
        transitionAnalysis: transitionAnalysis,
      };
    } catch (error) {
      console.error("Error in probation analysis:", error);
      throw error;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!organizationId) {
        setError("Organization ID is required");
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await calculateProbationAnalysis(organizationId);
        setProbationData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [organizationId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
        role="alert"
      >
        <strong className="font-bold">Error: </strong>
        <span className="block sm:inline">{error}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4 mb-6">
        <div className="h-10 w-1 bg-gradient-to-r from-purple-500 to-purple-600 rounded-full" />
        <h2 className="text-2xl font-bold text-white">
          Probation/Parole Analysis
        </h2>
      </div>

      {/* Overview Section */}
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-purple-400">
            Probation/Parole Impact Overview
          </h3>
          <span className="relative inline-block group">
            <Info
              size={16}
              className="text-slate-400 cursor-pointer hover:text-slate-300 transition-colors"
            />
            <span className="pointer-events-none absolute left-1/2 top-full mt-2 w-80 -translate-x-1/2 rounded-md bg-slate-800/95 p-3 text-xs leading-snug text-slate-200 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm z-50">
              <div className="space-y-2">
                <div>
                  <strong className="text-red-400">
                    Left Chart (Recidivism):
                  </strong>{" "}
                  Shows recidivism rates for alumni only, as recidivism can only
                  be measured after program completion.
                </div>
                <div>
                  <strong className="text-orange-400">Data Limitation:</strong>{" "}
                  Recidivism rates are close estimates, as tracking every alumni
                  for arrests is challenging and some incidents may go
                  unreported.
                </div>
                <div>
                  <strong className="text-green-400">
                    Right Chart (Distribution):
                  </strong>{" "}
                  Shows supervision status for all people (current residents +
                  alumni) to display overall program demographics.
                </div>
                <div>
                  <strong className="text-amber-400">Deduplication:</strong> All
                  people are counted only once.
                </div>
              </div>
            </span>
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column - Recidivism Rates Comparison */}
          <div className="bg-slate-900/50 p-4 rounded-lg">
            <h4 className="text-white font-semibold mb-4">Recidivism Rates</h4>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={[
                    {
                      name: "On Probation/Parole",
                      value:
                        probationData.recidivismAnalysis?.probationRecidivism
                          ?.rate || 0,
                      count:
                        probationData.recidivismAnalysis?.probationRecidivism
                          ?.recidivism || 0,
                      total:
                        probationData.recidivismAnalysis?.probationRecidivism
                          ?.total || 0,
                      fill: "#EF4444",
                    },
                    {
                      name: "Not Supervised",
                      value:
                        probationData.recidivismAnalysis?.noProbationRecidivism
                          ?.rate || 0,
                      count:
                        probationData.recidivismAnalysis?.noProbationRecidivism
                          ?.recidivism || 0,
                      total:
                        probationData.recidivismAnalysis?.noProbationRecidivism
                          ?.total || 0,
                      fill: "#10B981",
                    },
                  ]}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  // Remove the label prop to prevent cutoff
                  // label={({ name, value }) => `${name}: ${value.toFixed(1)}%`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1e293b",
                    border: "1px solid #334155",
                    borderRadius: "0.5rem",
                  }}
                  itemStyle={{ color: "#e2e8f0" }}
                  labelStyle={{ color: "#f8fafc", fontWeight: "bold" }}
                  formatter={(value, name, props) => [
                    `${value.toFixed(1)}% (${props.payload.count}/${
                      props.payload.total
                    })`,
                    name,
                  ]}
                />
                {/* Add Legend to show the labels */}
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  wrapperStyle={{
                    paddingTop: "10px",
                    fontSize: "12px",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Right Column - Overall Probation Distribution */}
          <div className="bg-slate-900/50 p-4 rounded-lg">
            <h4 className="text-white font-semibold mb-4">
              Overall Probation/Parole Distribution
            </h4>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={[
                    {
                      name: "On Probation/Parole",
                      value:
                        probationData.transitionAnalysis?.overallProbationStats
                          ?.probationPercentage || 0,
                      count:
                        probationData.transitionAnalysis?.overallProbationStats
                          ?.onProbationCount || 0,
                      fill: "#EF4444",
                    },
                    {
                      name: "Not Supervised",
                      value:
                        100 -
                        (probationData.transitionAnalysis?.overallProbationStats
                          ?.probationPercentage || 0),
                      count:
                        probationData.transitionAnalysis?.overallProbationStats
                          ?.notOnProbationCount || 0,
                      fill: "#10B981",
                    },
                  ]}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1e293b",
                    border: "1px solid #334155",
                    borderRadius: "0.5rem",
                  }}
                  itemStyle={{ color: "#e2e8f0" }}
                  labelStyle={{ color: "#f8fafc", fontWeight: "bold" }}
                  formatter={(value, name, props) => [
                    `${value.toFixed(1)}% (${props.payload.count} people)`,
                    name,
                  ]}
                />
                {/* Add Legend to show the labels */}
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  wrapperStyle={{
                    paddingTop: "10px",
                    fontSize: "12px",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="mt-4 bg-slate-900/50 p-4 rounded-lg">
          <h4 className="text-white font-semibold mb-2">Key Insights</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div className="text-center">
              <p className="text-2xl font-bold text-red-400">
                {probationData.recidivismAnalysis?.probationRecidivism?.rate?.toFixed(
                  1
                ) || 0}
                %
              </p>
              <p className="text-slate-400">Supervised Recidivism</p>
              <p className="text-xs text-slate-500">
                (
                {probationData.recidivismAnalysis?.probationRecidivism
                  ?.recidivism || 0}
                /
                {probationData.recidivismAnalysis?.probationRecidivism?.total ||
                  0}{" "}
                alumni)
              </p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-400">
                {probationData.recidivismAnalysis?.noProbationRecidivism?.rate?.toFixed(
                  1
                ) || 0}
                %
              </p>
              <p className="text-slate-400">Unsupervised Recidivism</p>
              <p className="text-xs text-slate-500">
                (
                {probationData.recidivismAnalysis?.noProbationRecidivism
                  ?.recidivism || 0}
                /
                {probationData.recidivismAnalysis?.noProbationRecidivism
                  ?.total || 0}{" "}
                alumni)
              </p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-400">
                {probationData.transitionAnalysis?.successfulTransitions || 0}
              </p>
              <p className="text-slate-400">Successful Transitions</p>
              <p className="text-xs text-slate-500">people off supervision</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-400">
                {probationData.transitionAnalysis?.currentlySupervised || 0}
              </p>
              <p className="text-slate-400">Currently Supervised</p>
              <p className="text-xs text-slate-500">current residents</p>
            </div>
          </div>
        </div>
      </div>

      {/* Time-Dependent Effects */}
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-purple-400">
            Recovery Phase Analysis
          </h3>
          <span className="relative inline-block group">
            <Info
              size={16}
              className="text-slate-400 cursor-pointer hover:text-slate-300 transition-colors"
            />
            <span className="pointer-events-none absolute left-1/2 top-full mt-2 w-80 -translate-x-1/2 rounded-md bg-slate-800/95 p-3 text-xs leading-snug text-slate-200 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm z-50">
              <div className="space-y-2">
                <div>
                  <strong className="text-purple-400">What this shows:</strong>{" "}
                  How probation/parole supervision affects outcomes across
                  different recovery phases. Each person counted only once per
                  phase.
                </div>
                <div>
                  <strong className="text-blue-400">Value:</strong> Identifies
                  which recovery phases benefit most from supervision or require
                  additional support.
                </div>
              </div>
            </span>
          </span>
        </div>

        <div className="space-y-4">
          {Object.entries(probationData.probationTimeEffects).map(
            ([period, data]) => {
              const periodNames = {
                earlyRecovery: "Early Recovery (0-90 days)",
                midRecovery: "Mid Recovery (91-180 days)",
                establishedRecovery: "Established Recovery (181-270 days)",
                longTermRecovery: "Long-term Recovery (271-365 days)",
              };

              return (
                <div key={period} className="bg-slate-900/50 p-4 rounded-lg">
                  <h4 className="text-white font-semibold mb-2">
                    {periodNames[period]}
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                    <div className="text-center">
                      <p className="text-sm text-slate-400">Supervised Group</p>
                      <p className="text-lg text-purple-400">
                        {data.probation?.recidivismRate?.toFixed(1) || 0}%
                      </p>
                      <p className="text-xs text-slate-500">Recidivism</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-slate-400">
                        Unsupervised Group
                      </p>
                      <p className="text-lg text-green-400">
                        {data.noProbation?.recidivismRate?.toFixed(1) || 0}%
                      </p>
                      <p className="text-xs text-slate-500">Recidivism</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-slate-400">Supervised RC</p>
                      <p className="text-lg text-blue-400">
                        {data.probation?.avgRC?.toFixed(1) || 0}
                      </p>
                      <p className="text-xs text-slate-500">Average Score</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-slate-400">Unsupervised RC</p>
                      <p className="text-lg text-cyan-400">
                        {data.noProbation?.avgRC?.toFixed(1) || 0}
                      </p>
                      <p className="text-xs text-slate-500">Average Score</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-slate-400">
                        Supervised People
                      </p>
                      <p className="text-lg text-yellow-400">
                        {data.probation?.uniqueResidents || 0}
                      </p>
                      <p className="text-xs text-slate-500">Unique Residents</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-slate-400">
                        Unsupervised People
                      </p>
                      <p className="text-lg text-orange-400">
                        {data.noProbation?.uniqueResidents || 0}
                      </p>
                      <p className="text-xs text-slate-500">Unique Residents</p>
                    </div>
                  </div>
                </div>
              );
            }
          )}
        </div>
      </div>

      {/* Risk Factor Interactions */}
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <h3 className="text-xl font-bold text-purple-400">
              Risk Factor Analysis for Supervised Residents
            </h3>
            <span className="relative inline-block group">
              <Info
                size={16}
                className="text-slate-400 cursor-pointer hover:text-slate-300 transition-colors"
              />
              <span className="pointer-events-none absolute left-1/2 top-full mt-2 w-80 -translate-x-1/2 rounded-md bg-slate-800/95 p-3 text-xs leading-snug text-slate-200 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm z-50">
                <div className="space-y-2">
                  <div>
                    <strong className="text-red-400">Data Limitation:</strong>{" "}
                    This analysis only includes supervised residents who have
                    completed at least one Recovery Capital questionnaire.
                  </div>
                  <div>
                    <strong className="text-amber-400">Sample Size:</strong> The
                    total count here may be smaller than the overall supervised
                    population shown in other charts.
                  </div>
                </div>
              </span>
            </span>
          </div>

          <div className="flex gap-2">
            {/* Metric Selection */}
            <div className="relative">
              <button
                onClick={() => setIsMetricDropdownOpen(!isMetricDropdownOpen)}
                className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg px-4 py-2 text-sm transition-all"
              >
                <span>
                  {metricOptions.find((opt) => opt.id === selectedMetric)
                    ?.name || "Select Metric"}
                </span>
                <ChevronDown size={16} />
              </button>

              {isMetricDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-slate-800 border border-slate-700 rounded-lg shadow-lg z-10">
                  <ul className="py-1">
                    {metricOptions.map((metric) => (
                      <li key={metric.id}>
                        <button
                          onClick={() => {
                            setSelectedMetric(metric.id);
                            setIsMetricDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm ${
                            selectedMetric === metric.id
                              ? "bg-slate-700 text-white"
                              : "text-slate-300 hover:bg-slate-700 hover:text-white"
                          }`}
                        >
                          {metric.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Risk Factor Selection */}
            <div className="relative">
              <button
                onClick={() =>
                  setIsRiskFactorDropdownOpen(!isRiskFactorDropdownOpen)
                }
                className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg px-4 py-2 text-sm transition-all"
              >
                <span>
                  {riskFactorOptions.find(
                    (opt) => opt.id === selectedRiskFactor
                  )?.name || "Select Factor"}
                </span>
                <ChevronDown size={16} />
              </button>

              {isRiskFactorDropdownOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-slate-800 border border-slate-700 rounded-lg shadow-lg z-10">
                  <ul className="py-1">
                    {riskFactorOptions.map((factor) => (
                      <li key={factor.id}>
                        <button
                          onClick={() => {
                            setSelectedRiskFactor(factor.id);
                            setIsRiskFactorDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-2 text-sm ${
                            selectedRiskFactor === factor.id
                              ? "bg-slate-700 text-white"
                              : "text-slate-300 hover:bg-slate-700 hover:text-white"
                          }`}
                        >
                          {factor.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Risk Factor Data Display */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          {/* --- BOX 1: WITH RISK FACTOR --- */}
          <div className="bg-slate-900/50 p-4 rounded-lg">
            <h4 className="text-white font-semibold mb-2">
              {
                riskFactorOptions.find((opt) => opt.id === selectedRiskFactor)
                  ?.withLabel
              }
            </h4>
            <div className="space-y-2">
              <div className="mb-2">
                <p className="text-2xl font-bold text-red-400">
                  {/* --- CORRECTED LOGIC FOR BOX 1 --- */}
                  {selectedMetric === "relapse"
                    ? `${
                        probationData.allRiskFactorInteractions?.[
                          selectedRiskFactor
                        ]?.probationWith?.relapseRate?.toFixed(1) || 0
                      }%`
                    : selectedMetric === "rcScore"
                    ? probationData.allRiskFactorInteractions?.[
                        selectedRiskFactor
                      ]?.probationWith?.avgRC?.toFixed(1) || 0
                    : `${
                        probationData.allRiskFactorInteractions?.[
                          selectedRiskFactor
                        ]?.probationWith?.recidivismRate?.toFixed(1) || 0
                      }%`}
                </p>
                <p className="text-xs text-slate-400">
                  {selectedMetric === "relapse"
                    ? "Relapse Rate"
                    : selectedMetric === "rcScore"
                    ? "Average RC Score"
                    : "Recidivism Rate"}
                </p>
              </div>
              <div className="text-sm text-slate-300">
                Unique People:{" "}
                {probationData.allRiskFactorInteractions?.[selectedRiskFactor]
                  ?.probationWith?.uniqueResidents || 0}
              </div>
            </div>
          </div>

          {/* --- BOX 2: WITHOUT RISK FACTOR --- */}
          <div className="bg-slate-900/50 p-4 rounded-lg">
            <h4 className="text-white font-semibold mb-2">
              {
                riskFactorOptions.find((opt) => opt.id === selectedRiskFactor)
                  ?.withoutLabel
              }
            </h4>
            <div className="space-y-2">
              <div className="mb-2">
                <p className="text-2xl font-bold text-green-400">
                  {/* --- CORRECTED LOGIC FOR BOX 2 --- */}
                  {selectedMetric === "relapse"
                    ? `${
                        probationData.allRiskFactorInteractions?.[
                          selectedRiskFactor
                        ]?.probationWithout?.relapseRate?.toFixed(1) || 0
                      }%`
                    : selectedMetric === "rcScore"
                    ? probationData.allRiskFactorInteractions?.[
                        selectedRiskFactor
                      ]?.probationWithout?.avgRC?.toFixed(1) || 0
                    : `${
                        probationData.allRiskFactorInteractions?.[
                          selectedRiskFactor
                        ]?.probationWithout?.recidivismRate?.toFixed(1) || 0
                      }%`}
                </p>
                <p className="text-xs text-slate-400">
                  {selectedMetric === "relapse"
                    ? "Relapse Rate"
                    : selectedMetric === "rcScore"
                    ? "Average RC Score"
                    : "Recidivism Rate"}
                </p>
              </div>
              <div className="text-sm text-slate-300">
                Unique People:{" "}
                {probationData.allRiskFactorInteractions?.[selectedRiskFactor]
                  ?.probationWithout?.uniqueResidents || 0}
              </div>
            </div>
          </div>
        </div>

        {/* --- RISK FACTOR IMPACT SUMMARY --- */}
        <div className="bg-slate-900/50 p-4 rounded-lg">
          <h4 className="text-white font-semibold mb-2">
            Risk Factor Impact Summary
          </h4>
          <p className="text-slate-300 text-sm">
            Among supervised residents, those with{" "}
            {riskFactorOptions
              .find((opt) => opt.id === selectedRiskFactor)
              ?.name.toLowerCase()}{" "}
            show {/* --- CORRECTED LOGIC FOR SUMMARY TEXT --- */}
            {selectedMetric === "relapse"
              ? (probationData.allRiskFactorInteractions?.[selectedRiskFactor]
                  ?.probationWith?.relapseRate || 0) >
                (probationData.allRiskFactorInteractions?.[selectedRiskFactor]
                  ?.probationWithout?.relapseRate || 0)
                ? "higher relapse rates"
                : "lower relapse rates"
              : selectedMetric === "rcScore"
              ? (probationData.allRiskFactorInteractions?.[selectedRiskFactor]
                  ?.probationWith?.avgRC || 0) >
                (probationData.allRiskFactorInteractions?.[selectedRiskFactor]
                  ?.probationWithout?.avgRC || 0)
                ? "higher Recovery Capital scores"
                : "lower Recovery Capital scores"
              : (probationData.allRiskFactorInteractions?.[selectedRiskFactor]
                  ?.probationWith?.recidivismRate || 0) >
                (probationData.allRiskFactorInteractions?.[selectedRiskFactor]
                  ?.probationWithout?.recidivismRate || 0)
              ? "higher recidivism rates"
              : "lower recidivism rates"}{" "}
            compared to those without this risk factor.{" "}
            <em>Note: Data is properly deduplicated by person.</em>
          </p>
        </div>
      </div>

      {/* Domain Impact Analysis */}
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-purple-400">
            Recovery Capital Domain Analysis
          </h3>
          <span className="relative inline-block group">
            <Info
              size={16}
              className="text-slate-400 cursor-pointer hover:text-slate-300 transition-colors"
            />
            <span className="pointer-events-none absolute left-1/2 top-full mt-2 w-80 -translate-x-1/2 rounded-md bg-slate-800/95 p-3 text-xs leading-snug text-slate-200 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm z-50">
              <div className="space-y-2">
                <div>
                  <strong className="text-purple-400">What this shows:</strong>{" "}
                  How probation/parole supervision affects different domains of
                  recovery capital development.
                </div>
                <div>
                  <strong className="text-green-400">Insight:</strong>{" "}
                  Identifies which recovery domains are enhanced or hindered by
                  supervision requirements.
                </div>
              </div>
            </span>
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-slate-900/50 p-4 rounded-lg">
            <h4 className="text-white font-semibold mb-4">Domain Comparison</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={probationData.domainImpacts} layout="vertical">
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.1)"
                />
                <XAxis
                  type="number"
                  stroke="#94a3b8"
                  tick={{ fill: "#94a3b8" }}
                />
                <YAxis
                  type="category"
                  dataKey="domain"
                  stroke="#94a3b8"
                  tick={{ fill: "#94a3b8", fontSize: 12 }}
                  width={120}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1e293b",
                    border: "1px solid #334155",
                    borderRadius: "0.5rem",
                  }}
                  itemStyle={{ color: "#e2e8f0" }}
                  labelStyle={{ color: "#f8fafc", fontWeight: "bold" }}
                  formatter={(value, name) => [
                    `${value.toFixed(1)}${
                      name.includes("Avg") ? " points" : ""
                    }`,
                    name,
                  ]}
                />
                <Legend />
                <Bar
                  dataKey="probationAvg"
                  name="Supervised Avg"
                  fill="#8B5CF6"
                />
                <Bar
                  dataKey="nonProbationAvg"
                  name="Unsupervised Avg"
                  fill="#10B981"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="space-y-3">
            <h4 className="text-white font-semibold">Domain Impact Analysis</h4>
            {probationData.domainImpacts.map((domain, index) => (
              <div key={index} className="bg-slate-800/50 p-3 rounded-lg">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-slate-300 font-medium">
                    {domain.domain}
                  </span>
                  <span
                    className={`${
                      domain.percentDiff >= 0
                        ? "text-green-400"
                        : "text-red-400"
                    } font-bold`}
                  >
                    {domain.percentDiff >= 0 ? "+" : ""}
                    {domain.percentDiff}%
                  </span>
                </div>
                <div className="flex justify-between text-sm text-slate-400">
                  <span>Supervised: {domain.probationAvg}</span>
                  <span>Unsupervised: {domain.nonProbationAvg}</span>
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {Math.abs(domain.percentDiff) < 5
                    ? "Minimal difference between groups"
                    : domain.percentDiff > 0
                    ? "Higher scores in supervised group"
                    : "Lower scores in supervised group"}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Longitudinal Progression Analysis */}
      <div className="bg-slate-800/50 backdrop-blur-xl rounded-xl p-6 border border-slate-700/50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-purple-400">
            Longitudinal Recovery Progression
          </h3>
          <span className="relative inline-block group">
            <Info
              size={16}
              className="text-slate-400 cursor-pointer hover:text-slate-300 transition-colors"
            />
            <span className="pointer-events-none absolute left-1/2 top-full mt-2 w-80 -translate-x-1/2 rounded-md bg-slate-800/95 p-3 text-xs leading-snug text-slate-200 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm z-50">
              <div className="space-y-2">
                <div>
                  <strong className="text-purple-400">What this shows:</strong>{" "}
                  Recovery progression over time comparing supervised vs
                  unsupervised residents across multiple recovery phases.
                </div>
                <div>
                  <strong className="text-green-400">Value:</strong> Shows
                  optimal intervention timing and identifies critical periods
                  for enhanced support.
                </div>
              </div>
            </span>
          </span>
        </div>

        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={Object.entries(probationData.probationTimeEffects).map(
                ([period, data], index) => {
                  const result = {
                    period:
                      period === "earlyRecovery"
                        ? "0-3m"
                        : period === "midRecovery"
                        ? "3-6m"
                        : period === "establishedRecovery"
                        ? "6-9m"
                        : "9-12m",
                    supervisionRC: data.probation?.avgRC || 0,
                    noSupervisionRC: data.noProbation?.avgRC || 0,
                    supervisionRelapse: data.probation?.recidivismRate || 0,
                    noSupervisionRelapse: data.noProbation?.recidivismRate || 0,
                    supervisionCount: data.probation?.uniqueResidents || 0,
                    noSupervisionCount: data.noProbation?.uniqueResidents || 0,
                  };

                  return result;
                }
              )}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.1)"
              />
              <XAxis dataKey="period" stroke="#94a3b8" />
              <YAxis
                yAxisId="left"
                orientation="left"
                stroke="#10B981"
                domain={[120, 160]}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                stroke="#EF4444"
                domain={[0, 50]}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div className="bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-4 text-slate-300">
                        <p className="text-purple-400 font-semibold mb-2">
                          {label}
                        </p>
                        <div className="space-y-1 text-sm">
                          <p>
                            Supervised RC:{" "}
                            <span className="text-green-400">
                              {data.supervisionRC.toFixed(1)}
                            </span>{" "}
                            (n={data.supervisionCount} people)
                          </p>
                          <p>
                            Unsupervised RC:{" "}
                            <span className="text-cyan-400">
                              {data.noSupervisionRC.toFixed(1)}
                            </span>{" "}
                            (n={data.noSupervisionCount} people)
                          </p>
                          <p>
                            Supervised Relapse:{" "}
                            <span className="text-red-400">
                              {data.supervisionRelapse.toFixed(1)}%
                            </span>
                          </p>
                          <p>
                            Unsupervised Relapse:{" "}
                            <span className="text-orange-400">
                              {data.noSupervisionRelapse.toFixed(1)}%
                            </span>
                          </p>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="supervisionRC"
                stroke="#10B981"
                name="Supervised RC"
                strokeWidth={2}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="noSupervisionRC"
                stroke="#06B6D4"
                name="Unsupervised RC"
                strokeWidth={2}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="supervisionRelapse"
                stroke="#EF4444"
                name="Supervised Relapse %"
                strokeWidth={2}
                strokeDasharray="5 5"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="noSupervisionRelapse"
                stroke="#F97316"
                name="Unsupervised Relapse %"
                strokeWidth={2}
                strokeDasharray="5 5"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-4 bg-slate-900/50 p-4 rounded-lg">
          <h4 className="text-white font-semibold mb-2">
            Progression Insights
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-green-400 font-medium mb-1">
                Recovery Capital Development:
              </p>
              <ul className="text-slate-300 space-y-1 list-disc pl-4">
                <li>Both groups show steady RC improvement over time</li>
                <li>
                  Supervised residents maintain consistent development
                  trajectory
                </li>
                <li>
                  Gap between groups narrows in established recovery phases
                </li>
              </ul>
            </div>
            <div>
              <p className="text-red-400 font-medium mb-1">
                Relapse Risk Patterns:
              </p>
              <ul className="text-slate-300 space-y-1 list-disc pl-4">
                <li>Early recovery shows highest risk for both groups</li>
                <li>Supervised residents show more gradual risk reduction</li>
                <li>
                  Both groups achieve similar low risk in long-term recovery
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RCProbationTab;
